import { toast } from '@/hooks/use-toast';

const recentToasts = new Map<string, number>();
const TOAST_COOLDOWN_MS = 30_000;

export interface ErrorInfo {
  code?: string;
  message: string;
  isRateLimitError: boolean;
  isAbortError: boolean;
  shouldRetry: boolean;
}

export function processError(error: any): ErrorInfo {
  const errorMessage = error?.message || 'An unexpected error occurred';
  
  // Check for 429 Rate Limit
  if (error?.status === 429 || errorMessage.includes('429') || errorMessage.includes('Too Many Requests')) {
    return {
      code: '429',
      message: 'Quá nhiều yêu cầu. Vui lòng chậm lại.',
      isRateLimitError: true,
      isAbortError: false,
      shouldRetry: false
    };
  }

  // Check for AbortError
  if (error?.name === 'AbortError' || errorMessage.includes('aborted')) {
    return {
      code: 'ABORT',
      message: 'Yêu cầu đã bị hủy',
      isRateLimitError: false,
      isAbortError: true,
      shouldRetry: false
    };
  }

  // Check for network errors that should retry
  if (error?.status >= 500 || errorMessage.includes('Network') || errorMessage.includes('fetch')) {
    return {
      code: error?.status?.toString() || 'NETWORK',
      message: 'Lỗi kết nối. Đang thử lại...',
      isRateLimitError: false,
      isAbortError: false,
      shouldRetry: true
    };
  }

  // Auth errors
  if (error?.status === 401 || error?.status === 403) {
    return {
      code: error?.status?.toString(),
      message: 'Không có quyền truy cập',
      isRateLimitError: false,
      isAbortError: false,
      shouldRetry: false
    };
  }

  return {
    code: error?.status?.toString() || 'UNKNOWN',
    message: errorMessage,
    isRateLimitError: false,
    isAbortError: false,
    shouldRetry: false
  };
}

export function handleQueryError(error: any, context?: string) {
  const errorInfo = processError(error);
  
  // Don't show toasts for abort errors or rate limits (already handled by request monitor)
  if (errorInfo.isAbortError) {
    console.log(`[${context}] Request aborted:`, error);
    return;
  }

  if (errorInfo.isRateLimitError) {
    console.warn(`[${context}] Rate limit hit:`, error);
    return;
  }

  // Suppress toast for retryable errors — React Query retries silently
  if (errorInfo.shouldRetry) {
    console.error(`[${context}] Retryable error (toast suppressed):`, error);
    return;
  }

  // Debounce same message within cooldown window
  const key = errorInfo.message;
  const now = Date.now();
  const lastShown = recentToasts.get(key) ?? 0;
  if (now - lastShown < TOAST_COOLDOWN_MS) {
    console.error(`[${context}] Error (toast suppressed, cooldown):`, error);
    return;
  }
  recentToasts.set(key, now);

  toast({
    title: "Lỗi",
    description: errorInfo.message,
    variant: "destructive"
  });

  console.error(`[${context}] Error:`, error);
}

export function createRetryStrategy(maxRetries: number = 1) {
  return (failureCount: number, error: any) => {
    const errorInfo = processError(error);
    
    // Don't retry rate limits, auth errors, or abort errors
    if (errorInfo.isRateLimitError || errorInfo.isAbortError || 
        error?.status === 401 || error?.status === 403) {
      return false;
    }

    return failureCount < maxRetries && errorInfo.shouldRetry;
  };
}

export function createRetryDelay(baseDelay: number = 2000) {
  return (attemptIndex: number, error: any) => {
    const errorInfo = processError(error);
    
    // Longer delay for rate limit errors (if they somehow get here)
    if (errorInfo.isRateLimitError) {
      return Math.min(30000, baseDelay * 4 * (2 ** attemptIndex));
    }

    // Standard exponential backoff
    return Math.min(10000, baseDelay * (2 ** attemptIndex));
  };
}